// Thread / chat: firm, client and system messages in one stream. Agent drafts are violet and marked "draft — not sent":
// a draft never leaves the firm until a person sends it (thread.send is human-only).
import { useId, useState, type FormEvent, type ReactNode } from "react";
import type { Message } from "../data/types";
import { fmtStamp } from "../lib/format";
import { AgentChip, Button, inputCls } from "./primitives";

export function Thread({ messages, viewer, onSend, sendLabel = "Send", disabledReason, renderActions, placeholder }: {
  messages: Message[]; viewer: "firm" | "client"; onSend?: (body: string) => Promise<void> | void; sendLabel?: string; disabledReason?: string | null;
  /** Per-message acts (staff: send a draft, make a review note…). */
  renderActions?: (m: Message) => ReactNode; placeholder?: string;
}) {
  const uid = useId();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!onSend || !body.trim()) return;
    setBusy(true); setErr(undefined);
    try { await onSend(body.trim()); setBody(""); } catch (x) { setErr((x as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-3">
      <ol className="space-y-2" aria-label="Messages">
        {messages.length === 0 && <li className="text-sm text-muted">No messages yet.</li>}
        {messages.map((m) => {
          const mine = m.direction === viewer;
          if (m.direction === "system") return (
            <li key={m.id} className="text-center text-xs text-muted">— {m.body}{m.attachments.length > 0 && viewer === "firm" ? ` (${m.attachments.map((a) => a.name).join(", ")})` : ""} · {fmtStamp(m.at)}{viewer === "firm" && !m.clientVisible ? " · firm only" : ""} —</li>
          );
          return (
            <li key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm ${m.draft || m.agent ? "border-agent-line bg-agent-bg" : mine ? "border-progress-line bg-progress-bg" : m.direction === "client" ? "border-client-line bg-client-bg" : "border-line bg-raised"}`}>
                <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                  <span className="font-medium text-ink">{m.author}</span>
                  {m.agent && <AgentChip name={m.agent} />}
                  {m.draft && <span className="font-medium text-agent">draft — not sent</span>}
                  {viewer === "firm" && m.direction === "client" && <span className="text-client">client's statement</span>}
                  {m.intent !== "message" && <span>{m.intent.replace(/_/g, " ")}</span>}
                  <span>{fmtStamp(m.at)}</span>
                </div>
                {viewer === "firm" && m.direction === "client" ? <blockquote className="whitespace-pre-wrap border-l-2 border-client-line pl-2">{m.body}</blockquote> : <p className="whitespace-pre-wrap">{m.body}</p>}
                {m.attachments.length > 0 && <p className="mt-1 text-xs text-muted">Attached: {m.attachments.map((a) => a.name).join(", ")}</p>}
                {renderActions?.(m)}
              </div>
            </li>
          );
        })}
      </ol>
      {onSend && (
        <form onSubmit={submit} className="space-y-2">
          <label className="sr-only" htmlFor={`${uid}-body`}>Message</label>
          <textarea id={`${uid}-body`} className={`${inputCls} min-h-[72px]`} value={body} onChange={(e) => setBody(e.target.value)} disabled={!!disabledReason || busy}
            placeholder={disabledReason ?? placeholder ?? "Write in your own words"} />
          {err && <p role="alert" className="text-xs text-blocked">{err}</p>}
          <div className="flex justify-end"><Button kind="primary" type="submit" disabled={!!disabledReason || busy || !body.trim()}>{sendLabel}</Button></div>
        </form>
      )}
    </div>
  );
}
